import { useMutation } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  FormField,
  Grid,
  Icon,
  Input,
  Label,
  Separator,
  Stack,
  Text,
  useToast,
} from '@eluve/components';

import {
  getExperimentByIdQuery,
  updateExperimentVariantMutation,
} from './operations';
import {
  CURRENT_CONFIG_VERSION,
  OutputTemplateOverride,
  PromptModelArgOverride,
  PromptTemplateOverride,
  SttProviderOverride,
  VariantConfig,
  VersionedVariantConfig,
} from './overrides';

const variantFormSchema = z.object({
  name: z.string().min(1, 'Variant name is required'),
  posthogVariantKey: z.string().min(1, 'PostHog variant key is required'),
});

type VariantFormValues = z.infer<typeof variantFormSchema>;

const parseVariantConfig = (jsonString: string | undefined): VariantConfig => {
  if (!jsonString) return {};
  try {
    const parsed = JSON.parse(jsonString);
    if (parsed && typeof parsed === 'object') {
      if ('version' in parsed && 'config' in parsed) {
        return (parsed as VersionedVariantConfig).config ?? {};
      }
      return parsed as VariantConfig;
    }
    return {};
  } catch {
    return {};
  }
};

interface EditVariantDialogProps {
  experimentId: string;
  variantId: string;
  defaultValues: VariantFormValues & { variantConfig?: string };
}

export const EditVariantDialog: React.FC<EditVariantDialogProps> = ({
  experimentId,
  variantId,
  defaultValues,
}) => {
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const initialConfig = parseVariantConfig(defaultValues.variantConfig);

  const [overrideConfig, setOverrideConfig] =
    useState<VariantConfig>(initialConfig);

  const [promptOutputType, setPromptOutputType] = useState<string | undefined>(
    undefined,
  );

  const isDynamicOutput = promptOutputType === 'DYNAMIC_OUTPUT';

  const hasExistingOutputTemplateOverride = Boolean(
    overrideConfig.OUTPUT_TEMPLATE_ID ||
    overrideConfig.OUTPUT_TEMPLATE_VARIANT_ID,
  );

  const shouldShowOutputTemplateOverride =
    isDynamicOutput ||
    (promptOutputType === undefined && hasExistingOutputTemplateOverride);

  // Only clear output template values when we've explicitly determined
  // the prompt is NOT DYNAMIC_OUTPUT (not when promptOutputType is still undefined)
  useEffect(() => {
    if (promptOutputType !== undefined && !isDynamicOutput) {
      setOverrideConfig((prev) => {
        if (
          prev.OUTPUT_TEMPLATE_ID === undefined &&
          prev.OUTPUT_TEMPLATE_VARIANT_ID === undefined
        ) {
          return prev;
        }
        const {
          OUTPUT_TEMPLATE_ID: _outputTemplateId,
          OUTPUT_TEMPLATE_VARIANT_ID: _outputTemplateVariantId,
          ...rest
        } = prev;
        return rest;
      });
    }
  }, [isDynamicOutput, promptOutputType]);

  const { control, formState, handleSubmit, reset } =
    useForm<VariantFormValues>({
      resolver: zodResolver(variantFormSchema),
      defaultValues: {
        name: defaultValues.name,
        posthogVariantKey: defaultValues.posthogVariantKey,
      },
    });

  const [updateVariant] = useMutation(updateExperimentVariantMutation, {
    refetchQueries: [
      { query: getExperimentByIdQuery, variables: { experimentId } },
    ],
    onCompleted: () => {
      setOpen(false);
    },
    onError: () => {
      toast.error('Failed to update variant');
    },
  });

  const handleConfigChange = useCallback((updates: Partial<VariantConfig>) => {
    setOverrideConfig((prev) => {
      const newConfig = { ...prev };
      for (const [key, value] of Object.entries(updates)) {
        if (value === undefined) {
          delete newConfig[key as keyof VariantConfig];
        } else {
          (newConfig as Record<string, unknown>)[key] = value;
        }
      }
      return newConfig;
    });
  }, []);

  const handleUpdateVariant = handleSubmit((data) => {
    const cleanedConfig = Object.fromEntries(
      Object.entries(overrideConfig).filter(([, v]) => v !== undefined),
    );

    const versionedConfig: VersionedVariantConfig = {
      version: CURRENT_CONFIG_VERSION,
      publishedAt: new Date().toISOString(),
      config: cleanedConfig,
    };

    return updateVariant({
      variables: {
        variantId,
        name: data.name,
        variantConfig: versionedConfig,
      },
    });
  });

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      reset({
        name: defaultValues.name,
        posthogVariantKey: defaultValues.posthogVariantKey,
      });
      setOverrideConfig(initialConfig);
      setPromptOutputType(undefined);
    }
  };

  const handlePromptOutputTypeChange = useCallback(
    (outputType: string | undefined) => {
      setPromptOutputType(outputType);
    },
    [],
  );

  const hasOverrides = Object.keys(overrideConfig).length > 0;
  const requiresOutputTemplate =
    isDynamicOutput && !overrideConfig.OUTPUT_TEMPLATE_ID;
  const canSubmit =
    formState.isValid &&
    (formState.isDirty || hasOverrides) &&
    !requiresOutputTemplate;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Icon name="Pencil" size="auto" />
        </Button>
      </DialogTrigger>
      <DialogContent size="full" className="flex h-[90vh] w-[90vw] flex-col">
        <DialogHeader>
          <DialogTitle>Edit Variant</DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <Stack gap={4}>
            <Grid columns={1} gap={4} className="md:grid-cols-2">
              <Label>
                <Stack gap={2}>
                  Name *
                  <FormField
                    control={control}
                    name="name"
                    render={({ field }) => (
                      <Input
                        placeholder="e.g., Control, Treatment A"
                        {...field}
                      />
                    )}
                  />
                </Stack>
              </Label>

              <Label>
                <Stack gap={2}>
                  PostHog Variant Key
                  <FormField
                    control={control}
                    name="posthogVariantKey"
                    render={({ field }) => (
                      <Input
                        placeholder="e.g., control, test, variant-a"
                        {...field}
                        disabled
                      />
                    )}
                  />
                  <Text size="xs" color="tertiary">
                    Variant key is synced from PostHog
                  </Text>
                </Stack>
              </Label>
            </Grid>

            <Separator className="my-2" />

            <Stack gap={3}>
              <Text size="md" color="secondary">
                Component Overrides
              </Text>

              <SttProviderOverride
                config={overrideConfig}
                onChange={handleConfigChange}
              />

              <PromptTemplateOverride
                config={overrideConfig}
                onChange={handleConfigChange}
                onOutputTypeChange={handlePromptOutputTypeChange}
              />

              {shouldShowOutputTemplateOverride && (
                <Stack gap={1}>
                  <OutputTemplateOverride
                    config={overrideConfig}
                    onChange={handleConfigChange}
                  />
                  {requiresOutputTemplate && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        Output template is required when using DYNAMIC_OUTPUT
                      </AlertDescription>
                    </Alert>
                  )}
                </Stack>
              )}

              <PromptModelArgOverride
                config={overrideConfig}
                onChange={handleConfigChange}
              />
            </Stack>
          </Stack>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateVariant}
            disabled={formState.isSubmitting || !canSubmit}
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
